/**
 * 前ゼロ 4 桁に整形する
 *
 * @param {String|Number} n 値
 * @returns {String} 前ゼロ 4 桁に整形した文字列を返す
 */
export function z4 (n) {
  return ('0000' + n).slice(-4)
}

/**
 * 前ゼロ 3 桁に整形する
 *
 * @param {String|Number} n 値
 * @returns {String} 前ゼロ 3 桁に整形した文字列を返す
 */
export function z3 (n) {
  return ('000' + n).slice(-3)
}

/**
 * 前ゼロ 2 桁に整形する
 *
 * @param {String|Number} n 値
 * @returns {String} 前ゼロ 2 桁に整形した文字列を返す
 */
export function z2 (n) {
  return ('00' + n).slice(-2)
}

/**
 * 指定時間後に、resolve する
 *
 * @param {Number} msec 待ち時間(ミリ秒)
 * @returns {Promise} 待ち時間を通知する Promise を返す
 */
export function wait (msec) {
  return new Promise(resolve => {
    setTimeout(() => resolve(), msec)
  })
}

/**
 * Date オブジェクトを作成する
 *
 * 日時を示すデータから Date オブジェクトを作成する。
 * 日時情報 は '1900-01-01' などの文字列や、unix epoch の数値、日付情報が含まれるオブジェクトを指定できる。
 *
 * 文字列の例: '1900-01-02', '1900-01-02 15:04', '1900-01-02 15:04:05'
 * オブジェクトの例: { y: 1900, m: 1, d: 2, h: 15, i: 4, s: 5 }
 *
 * @param {String|Number|Date|Object?} time 日時情報
 * @return {Date} 作成したDate オブジェクトを返す
 */
export function create (time) {
  if (!time && time !== 0) {
    return null
  } else if (typeof time === 'string') {
    return createFromString(time)
  } else if (typeof time === 'number') {
    return new Date(time)
  } else if (time instanceof Date) {
    return time
  } else if (typeof time === 'object') {
    return createFromParams(time)
  }
}

/**
 * 日時を示すオブジェクトから Date オブジェクトを作成する
 *
 * @param {Object} time 日時を示すオブジェクト
 * @returns {Date} 作成したDate オブジェクトを返す
 */
function createFromParams (time) {
  const now = new Date()

  if (time.y !== undefined) {
    // y が指定されている
    time.y = time.y | 0
    time.m = (time.m !== undefined) ? (time.m | 0) : 1
    time.d = (time.d !== undefined) ? (time.d | 0) : 1
  } else if (time.m !== undefined) {
    // m が指定されている
    time.y = now.getFullYear()
    time.m = time.m | 0
    time.d = (time.d !== undefined) ? (time.d | 0) : 1
  } else if (time.d !== undefined) {
    // d が指定されている
    time.y = now.getFullYear()
    time.m = now.getMonth() + 1
    time.d = time.d | 0
  } else {
    // y, m, d が指定されていない
    time.y = 1970
    time.m = 1
    time.d = 1
  }

  time.h = time.h | 0
  time.i = time.i | 0
  time.s = time.s | 0
  time.v = time.v | 0

  return new Date(time.y, time.m - 1, time.d, time.h, time.i, time.s, time.v)
}

const patItems = {
  Y: '(?<y>\\d{4})',
  m: '(?<m>\\d{1,2})',
  d: '(?<d>\\d{1,2})',
  H: '(?<h>\\d{1,2})',
  i: '(?<i>\\d{1,2})',
  s: '(?<s>\\d{1,2})',
  v: '(?<v>\\d{3})'
}

const reFmt = /[YmdHisv]/g

/**
 * 日付文字列を解析する正規表現を作成する
 *
 * @param {String} pat パターン文字列
 * @returns {RegExp} 正規表現オブジェクトを返す
 */
function makePattern (pat) {
  const s = pat.replace(reFmt, w => patItems[w])
  return new RegExp(`^${s}$`)
}

/**
 * 日付文字列を解析する正規表現を作成する
 *
 * @param {Array} v パターン文字列の配列
 * @returns {Array} 正規表現オブジェクトの配列を返す
 */
function makePatterns (v) {
  return v.map(pat => makePattern(pat))
}

/**
 * createFromString() で受け付ける文字列の書式
 */
const patterns = makePatterns([
  'Y-m-d H:i:s',
  'Y-m-d H:i',
  'Y-m-d',

  'Y/m/d H:i:s',
  'Y/m/d H:i',
  'Y/m/d'
])

const reJson = makePattern('Y-m-dTH:i:s.vZ')

/**
 * 日時を示す文字列から Date オブジェクトを作成する
 *
 * @param {Object} time 日時を示す文字列
 * @returns {Date} 作成したDate オブジェクトを返す
 */
function createFromString (time) {
  if (reJson.test(time)) {
    return new Date(time)
  }

  for (let i = 0, len = patterns.length; i < len; ++i) {
    const m = time.match(patterns[i])
    if (m) {
      return createFromParams(m.groups)
    }
  }
  throw new Error(`Could not recognize time string '${time}'`)
}

/**
 * Date オブジェクトを複製する
 *
 * @param {Date} d 元の Date オブジェクト
 * @returns 複製した Date オブジェクト
 */
export function clone (d) {
  return new Date(d.valueOf())
}

/**
 * 現在日時の Date オブジェクトを作成する
 *
 * @returns {Date} 現在日時の Date オブジェクト返す
 */
export function now () {
  return new Date()
}

/**
 * 今日の Date オブジェクトを作成する
 *
 * @returns {Date} 今日の Date オブジェクト返す
 */
export function today () {
  const tm = new Date()
  tm.setHours(0, 0, 0, 0)
  return tm
}

/**
 * 指定期間の間隔を ymd 形式で返す
 *
 * @param {*} to 期間終了日
 * @param {*} from 期間開始日
 * @param {Number} 期間の間隔を 年*10000 + 月*100 + 日 で返す
 */
export function duration (to, from) {
  let end = create(to)
  let begin = create(from)
  let sign = 1

  if (end < begin) {
    // begin, end を入れ替える
    const t = end
    end = begin
    begin = t

    // 符号をマイナスにする
    sign = -1
  }

  // begin の月末日を得る
  const lastday = clone(begin)
  lastday.setMonth(lastday.getMonth() + 1, 0)

  // 年月日をそれぞれ減算する
  let y = end.getFullYear() - begin.getFullYear()
  let m = end.getMonth() - begin.getMonth()
  let d = end.getDate() - begin.getDate()

  // 繰り上がりを処理する
  if (d < 0) {
    m -= 1
    d += lastday.getDate()
  }
  if (m < 0) {
    y -= 1
    m += 12
  }

  // ymd 形式にする
  return sign * (y * 10000 + m * 100 + d)
}
